
import { AdminRouteGuard } from "@/components/admin/AdminRouteGuard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminUsers } from "@/hooks/useAdminUsers";
import BasicInformationCard from "@/components/dashboard/listings/BasicInformationCard";
import LocationInformationCard from "@/components/dashboard/listings/LocationInformationCard";
import PropertyDetailsCard from "@/components/dashboard/listings/PropertyDetailsCard";
import AmenitiesSection from "@/components/dashboard/listings/AmenitiesSection";
import { useForm } from "react-hook-form";
import { ListingFormValues, listingFormSchema } from "@/components/listings/filters/FilterSchema";
import { zodResolver } from "@hookform/resolvers/zod";

export default function CreateListingPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: userData } = useAdminUsers();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  
  const form = useForm<ListingFormValues>({
    resolver: zodResolver(listingFormSchema),
    defaultValues: {
      title: "",
      description: "",
      propertyType: "",
      listing_type: "",
      price: 0,
      location: {
        address: "",
        city: "",
        state: "",
        zip: "",
        country: "Sri Lanka"
      },
      area: undefined,
      bathrooms: undefined,
      bedrooms: undefined,
      parking: undefined,
      amenities: []
    }
  });
  
  const handleSubmit = async (values: ListingFormValues) => {
    if (!selectedUserId) {
      toast.error("Please select an owner for this listing");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Add the selected user ID to the form data
      const listingData = {
        ...values,
        user_id: selectedUserId
      };
      
      // Here you would call your API to create the listing
      console.log("Creating listing with data:", listingData);
      
      toast.success("Listing created successfully");
      navigate("/admin/listings");
    } catch (error) {
      toast.error("Failed to create listing. Please try again.");
      console.error("Error creating listing:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminRouteGuard requiredRole="superadmin">
      <div className="container py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">
            Create New Listing
          </h1>
          <p className="text-muted-foreground">
            Add a new property listing to the platform
          </p>
        </div>

        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <Tabs defaultValue="basic">
            <div className="mb-6">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="basic">Basic Information</TabsTrigger>
                <TabsTrigger value="location">Location</TabsTrigger>
                <TabsTrigger value="details">Property Details</TabsTrigger>
                <TabsTrigger value="amenities">Amenities & Features</TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="basic" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Select Listing Owner</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Choose which user will be the owner of this property listing
                  </p>
                  
                  <select
                    className="w-full p-2 border rounded-md"
                    onChange={(e) => setSelectedUserId(Number(e.target.value))}
                    value={selectedUserId || ""}
                  >
                    <option value="">-- Select a user --</option>
                    {userData?.users?.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email}) - {user.role}
                      </option>
                    ))}
                  </select>
                </CardContent>
              </Card>
              
              <BasicInformationCard form={form} />
            </TabsContent>
            
            <TabsContent value="location">
              <LocationInformationCard form={form} />
            </TabsContent>
            
            <TabsContent value="details">
              <PropertyDetailsCard form={form} />
            </TabsContent>
            
            <TabsContent value="amenities">
              <AmenitiesSection form={form} />
            </TabsContent>
            
            <div className="flex justify-end gap-4 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/admin/listings")}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Listing"}
              </Button>
            </div>
          </Tabs>
        </form>
      </div>
    </AdminRouteGuard>
  );
}
